import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
  Business as BrandIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/templates/layout/AdminLayout';
import api from '../../services/api';

const AdminTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    type: 'newsletter',
    html: '',
    css: '',
    variables: [],
    isDefault: false,
    isGlobal: false,
    isActive: true,
    allowedBrands: [],
    allowedUsers: []
  });

  useEffect(() => {
    loadTemplates();
    loadBrands();
    loadUsers();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/newsletter/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplate({
      name: '',
      description: '',
      type: 'newsletter',
      html: getDefaultTemplate(),
      css: getDefaultCSS(),
      variables: [],
      isDefault: false,
      isGlobal: false,
      isActive: true,
      allowedBrands: [],
      allowedUsers: []
    });
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleEditTemplate = (templateItem) => {
    setEditingTemplate(templateItem);
    setTemplate({
      ...templateItem,
      allowedBrands: templateItem.allowedBrands || [],
      allowedUsers: templateItem.allowedUsers || []
    });
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      if (editingTemplate) {
        await api.put(`/newsletter/templates/${editingTemplate._id}`, template);
      } else {
        await api.post('/newsletter/templates', template);
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await api.delete(`/newsletter/templates/${id}`);
        loadTemplates();
      } catch (error) {
        console.error('Erro ao excluir template:', error);
        alert('Erro ao excluir template: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const getDefaultTemplate = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{subject}}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; text-align: center; }
    .content { padding: 20px; background: #f9f9f9; }
    .article { background: white; margin: 20px 0; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .article h3 { color: #333; margin-top: 0; }
    .article p { color: #666; }
    .article a { color: #667eea; text-decoration: none; font-weight: bold; }
    .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Mensure Monitor</h1>
      <h2>{{subject}}</h2>
    </div>
    
    <div class="content">
      <p>Olá {{recipientName}},</p>
      
      {{#if message}}
      <p>{{message}}</p>
      {{/if}}
      
      <h3>Artigos Selecionados:</h3>
      
      {{#each articles}}
      <div class="article">
        <h3>{{title}}</h3>
        {{#if description}}
        <p>{{description}}</p>
        {{/if}}
        <p><a href="{{publicUrl}}">Ler artigo completo →</a></p>
      </div>
      {{/each}}
      
      <p>Obrigado por acompanhar o Mensure Monitor!</p>
    </div>
    
    <div class="footer">
      <p>Este email foi enviado pelo Mensure Monitor</p>
      <p><a href="{{unsubscribeUrl}}">Cancelar inscrição</a></p>
    </div>
  </div>
</body>
</html>`;
  };

  const getDefaultCSS = () => {
    return `
/* Estilos personalizados para o template */
.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.article {
  transition: transform 0.2s ease;
}

.article:hover {
  transform: translateY(-2px);
}

.footer {
  border-top: 1px solid #eee;
  margin-top: 20px;
}`;
  };

  const renderTemplatePreview = () => {
    if (!template.html) return null;
    const previewDoc = template.html.replace(/\{\{.*?\}\}/g, '[VARIÁVEL]');
    
    return (
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          Preview do Template:
        </Typography>
        <Box 
          sx={{ 
            maxHeight: 400, 
            overflow: 'auto',
            border: '1px solid #ccc',
            bgcolor: 'white',
            p: 2
          }}>
          <iframe
            title="Template Preview"
            srcDoc={previewDoc}
            style={{ width: '100%', height: 360, border: 'none', background: '#fff' }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <PaletteIcon color="primary" />
            Gerenciador de Templates de Newsletter
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Crie e gerencie templates de email para newsletters. Controle quais marcas podem usar cada template.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
          >
            Novo Template
          </Button>
        </Box>

        {loading && templates.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
            <Typography>Carregando templates...</Typography>
          </Paper>
        ) : templates.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Nenhum template cadastrado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Crie seu primeiro template de newsletter.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
            >
              Criar Template
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {templates.map((t) => (
              <Grid item xs={12} md={6} lg={4} key={t._id}>
                <Card elevation={0} sx={{ 
                  borderRadius: 3, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  transition: 'all 0.2s', 
                  '&:hover': { 
                    borderColor: 'primary.main', 
                    transform: 'translateY(-2px)', 
                    boxShadow: 2 
                  } 
                }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6">
                        {t.name}
                      </Typography>
                      <Box>
                        {t.isDefault && <Chip label="Padrão" size="small" color="primary" sx={{ mr: 0.5 }} />}
                        {!t.isActive && <Chip label="Inativo" size="small" color="error" />}
                        {t.isActive && !t.isDefault && <Chip label="Ativo" size="small" color="success" />}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ minHeight: 40, mb: 1 }}>
                      {t.description || 'Sem descrição'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tipo: {t.type}
                    </Typography>
                    
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Marcas Permitidas */}
                      {t.allowedBrands && t.allowedBrands.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <BrandIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {t.allowedBrands.slice(0, 2).map((brand, idx) => (
                            <Chip 
                              key={`brand-${idx}`} 
                              label={typeof brand === 'object' ? brand.name : 'Marca'} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          ))}
                          {t.allowedBrands.length > 2 && (
                            <Chip 
                              label={`+${t.allowedBrands.length - 2}`} 
                              size="small" 
                              sx={{ fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      )}
                      {/* Usuários Permitidos */}
                      {t.allowedUsers && t.allowedUsers.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {t.allowedUsers.slice(0, 2).map((user, idx) => (
                            <Chip 
                              key={`user-${idx}`} 
                              label={typeof user === 'object' ? user.name : 'Usuário'} 
                              size="small" 
                              variant="filled"
                              color="default"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          ))}
                          {t.allowedUsers.length > 2 && (
                            <Chip 
                              label={`+${t.allowedUsers.length - 2}`} 
                              size="small" 
                              sx={{ fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      )}
                      {/* Status */}
                      {t.isGlobal ? (
                        <Chip 
                          label="🌐 Global" 
                          size="small" 
                          color="success" 
                          sx={{ fontSize: '0.65rem', width: 'fit-content' }}
                        />
                      ) : (
                        <Chip 
                          label="🔒 Específico" 
                          size="small" 
                          color="warning" 
                          sx={{ fontSize: '0.65rem', width: 'fit-content' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<PreviewIcon />}
                      onClick={() => {
                        setTemplate(t);
                        setPreviewDialogOpen(true);
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditTemplate(t)}
                    >
                      Editar
                    </Button>
                    {!t.isDefault && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(t._id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Dialog de Edição */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {editingTemplate ? 'Editar Template' : 'Novo Template'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Configurações" />
                <Tab label="HTML" icon={<CodeIcon />} />
                <Tab label="CSS" icon={<PaletteIcon />} />
                <Tab label="Preview" icon={<PreviewIcon />} />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nome do Template"
                        value={template.name}
                        onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={template.type}
                          onChange={(e) => setTemplate({ ...template, type: e.target.value })}
                          label="Tipo"
                        >
                          <MenuItem value="newsletter">Newsletter</MenuItem>
                          <MenuItem value="promotional">Promocional</MenuItem>
                          <MenuItem value="announcement">Anúncio</MenuItem>
                          <MenuItem value="custom">Personalizado</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Descrição"
                        value={template.description}
                        onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.isDefault}
                            onChange={(e) => setTemplate({ ...template, isDefault: e.target.checked })}
                          />
                        }
                        label="Template Padrão"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.isActive}
                            onChange={(e) => setTemplate({ ...template, isActive: e.target.checked })}
                          />
                        }
                        label="Ativo"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Visibilidade
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.isGlobal === true}
                            onChange={(e) => setTemplate({ ...template, isGlobal: e.target.checked })}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Template Global</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {template.isGlobal ? 'Este template aparece para todos os usuários' : 'Este template é específico (apenas para marcas/usuários selecionados)'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 2 }}>
                        <BrandIcon fontSize="small" />
                        Marcas Permitidas
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {template.isGlobal 
                          ? 'Selecione marcas específicas que terão acesso a este template. Se nenhuma for selecionada, todas as marcas terão acesso.'
                          : 'Selecione as marcas que poderão ver este template.'
                        }
                      </Typography>
                      <Autocomplete
                        multiple
                        options={brands}
                        getOptionLabel={(option) => option.name}
                        value={brands.filter(b => 
                          template.allowedBrands?.some(ab => 
                            (typeof ab === 'object' ? ab._id : ab) === b._id
                          )
                        )}
                        onChange={(event, newValue) => {
                          setTemplate({ 
                            ...template, 
                            allowedBrands: newValue.map(v => v._id) 
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione as marcas..."
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        )}
                        ChipProps={{
                          size: 'small'
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 2 }}>
                        <PersonIcon fontSize="small" />
                        Usuários Permitidos
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Selecione os usuários específicos que poderão ver este template.
                      </Typography>
                      <Autocomplete
                        multiple
                        options={users}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        value={users.filter(u => 
                          template.allowedUsers?.some(au => 
                            (typeof au === 'object' ? au._id : au) === u._id
                          )
                        )}
                        onChange={(event, newValue) => {
                          setTemplate({ 
                            ...template, 
                            allowedUsers: newValue.map(v => v._id) 
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Selecione os usuários..."
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {option.name} ({option.email})
                          </li>
                        )}
                        ChipProps={{
                          size: 'small'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={20}
                    label="HTML do Template"
                    value={template.html}
                    onChange={(e) => setTemplate({ ...template, html: e.target.value })}
                    sx={{ fontFamily: 'monospace' }}
                  />
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Use variáveis como: {'{{subject}}'}, {'{{recipientName}}'}, {'{{message}}'}
                  </Alert>
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    label="CSS Personalizado"
                    value={template.css}
                    onChange={(e) => setTemplate({ ...template, css: e.target.value })}
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ mt: 3 }}>
                  {renderTemplatePreview()}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveTemplate}
              disabled={loading || !template.name}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Preview */}
        <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Preview: {template.name}</DialogTitle>
          <DialogContent>
            {(() => {
              const previewDoc = (template.html || '').replace(/\{\{.*?\}\}/g, '[VARIÁVEL]');
              return (
                <Box 
                  sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    p: 2, 
                    bgcolor: '#f9f9f9',
                    maxHeight: 500,
                    overflow: 'auto'
                  }}>
                  <iframe
                    title="Template Preview Dialog"
                    srcDoc={previewDoc}
                    style={{ width: '100%', height: 460, border: 'none', background: '#fff' }}
                  />
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AdminLayout>
  );
};

export default AdminTemplateManager;
